import React, { useEffect, useMemo, useState } from 'react';
import { addDays, addMinutesWall, formatRange, wallDate, wallDiffMinutes, wallTime } from './calendarTime';
import { openClientInMain, openFormInAcord, formHref } from './calendarFollowup';
import { CloseX, useCloseGuard } from './calendarModal';
import NotesReader, { ExpandButton } from './NotesReader';

// 5.43.0: closes only by its buttons (Save / Cancel / Close / ✕ — not by clicking outside, not by Esc); unsaved changes
// ask "Discard changes?". "⤢ Expand" opens the notes full-page (NotesReader); saving there uses this window's save.
// 5.32.0 Calendar: create / view / edit one event. Editing a repeating event edits the whole series (v1).
// Only the fields the user changed are sent (start + end + all_day always travel together); the server keeps
// everything else in the event exactly as it was. All-day end dates are shown INCLUSIVE here and sent exclusive.

export const REMINDER_OPTIONS = [
  ['', 'None'], ['0', 'At start'], ['5', '5 minutes before'], ['10', '10 minutes before'], ['15', '15 minutes before'],
  ['30', '30 minutes before'], ['60', '1 hour before'], ['120', '2 hours before'], ['1440', '1 day before'],
  ['2880', '2 days before'], ['10080', '1 week before'],
];
const REPEAT_OPTIONS = [['none', 'Does not repeat'], ['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly'], ['yearly', 'Yearly']];

// Server detail -> form values.
export function toForm(d) {
  const allDay = !!d.all_day;
  return {
    title: d.title || '',
    allDay,
    startDate: wallDate(d.start),
    startTime: allDay ? '09:00' : wallTime(d.start),
    endDate: allDay ? addDays(wallDate(d.end || d.start), -1) : wallDate(d.end || d.start),
    endTime: allDay ? '10:00' : wallTime(d.end || d.start),
    location: d.location || '',
    notes: d.notes || '',
    reminder: d.reminder_custom && d.reminder_minutes == null ? 'custom' : (d.reminder_minutes == null ? '' : String(d.reminder_minutes)),
    reminderCustom: !!d.reminder_custom,
    repeat: d.repeat || 'none',
    calendarId: d.calendar ? d.calendar.id : d.calendar_id,
  };
}

// Form values -> start/end wall strings (end exclusive for all-day).
export function formTimes(f) {
  if (f.allDay) {
    const endIncl = f.endDate && f.endDate >= f.startDate ? f.endDate : f.startDate;
    return { all_day: true, start: f.startDate, end: addDays(endIncl, 1) };
  }
  return { all_day: false, start: `${f.startDate}T${f.startTime || '00:00'}:00`, end: `${f.endDate || f.startDate}T${f.endTime || f.startTime || '00:00'}:00` };
}

// Only what changed (start/end/all_day together).
export function changedFields(initial, f) {
  const out = {};
  if (f.title !== initial.title) out.title = f.title;
  if (f.location !== initial.location) out.location = f.location;
  if (f.notes !== initial.notes) out.notes = f.notes;
  const a = formTimes(initial), b = formTimes(f);
  if (a.all_day !== b.all_day || a.start !== b.start || a.end !== b.end) Object.assign(out, b);
  if (f.repeat !== initial.repeat && f.repeat !== 'custom') out.repeat = f.repeat;
  if (f.reminder !== initial.reminder && f.reminder !== 'custom') out.reminder_minutes = f.reminder === '' ? null : Number(f.reminder);
  return out;
}

const C = { bg: '#1a1a1a', panel: '#242424', border: '#3a3a3a', text: '#f0f0f0', dim: '#aaa', violet: '#7c3aed' };
const input = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 6, background: C.bg, color: C.text, fontSize: 13, fontFamily: 'inherit' };
const label = { fontSize: 11, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, display: 'block' };
const btn = (bg, disabled) => ({ padding: '7px 14px', border: 'none', borderRadius: 6, background: bg, color: '#fff', fontSize: 13, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 });

export default function CalendarEventDialog({
  mode, detail, createDefaults, calendarChoices, canCopyToMine, busy, error, notice,
  onSave, onDelete, onCopy, onClose,
}) {
  const initial = useMemo(() => (mode === 'create' ? createDefaults : toForm(detail)), [mode, detail, createDefaults]);
  const [f, setF] = useState(initial);
  useEffect(() => { setF(initial); }, [initial]);
  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setF((p) => {
      const n = { ...p, [k]: v };
      // moving the start keeps the length (timed) / the day span (all-day)
      if (k === 'startDate' || k === 'startTime') {
        if (p.allDay) {
          const span = Math.max(0, (new Date(`${p.endDate}T00:00:00Z`) - new Date(`${p.startDate}T00:00:00Z`)) / 86400000);
          if (n.startDate) n.endDate = addDays(n.startDate, span);
        } else if (p.startDate && p.startTime && n.startDate && n.startTime) {
          const mins = wallDiffMinutes(`${p.startDate}T${p.startTime}:00`, `${p.endDate}T${p.endTime}:00`);
          const ne = addMinutesWall(`${n.startDate}T${n.startTime}:00`, Math.max(0, mins));
          n.endDate = ne.slice(0, 10); n.endTime = ne.slice(11, 16);
        }
      }
      return n;
    });
  };

  const readOnly = mode !== 'create' && !(detail && detail.can_edit);
  const isEdit = mode !== 'create';
  const cal = isEdit ? detail.calendar : null;
  const changes = isEdit ? changedFields(initial, f) : null;
  const times = formTimes(f);
  const badRange = !f.startDate || (!f.allDay && times.end < times.start);
  const canSave = !busy && !readOnly && !badRange && (mode === 'create' || Object.keys(changes).length > 0);
  const dirty = !readOnly && (mode === 'create' ? JSON.stringify(f) !== JSON.stringify(initial) : Object.keys(changes).length > 0);
  const { requestClose, prompt } = useCloseGuard(dirty, onClose);
  const [reader, setReader] = useState(false);
  const readerSave = (text) => {             // create: back into the form; edit: the normal save (all changes in this window)
    setF((p) => ({ ...p, notes: text }));
    if (mode !== 'create') onSave(changedFields(initial, { ...f, notes: text }));
  };

  const save = () => {
    if (!canSave) return;
    if (mode === 'create') {
      onSave({ calendar_id: Number(f.calendarId), title: f.title, ...times, location: f.location, notes: f.notes,
        reminder_minutes: f.reminder === '' ? null : Number(f.reminder), repeat: f.repeat });
    } else {
      onSave(changes);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      data-testid="cal-event-overlay">
      <div role="dialog" aria-label={mode === 'create' ? 'New event' : 'Event'} data-testid="cal-event-dialog"
        style={{ background: C.panel, color: C.text, borderRadius: 10, width: 520, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', padding: 22, boxShadow: '0 10px 40px rgba(0,0,0,0.5)', border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{mode === 'create' ? 'New event' : readOnly ? 'Event' : 'Edit event'}</div>
          <div style={{ flex: 1 }} />
          {cal && <span style={{ fontSize: 12, color: C.dim }}>{cal.name}</span>}
          <CloseX onClick={requestClose} disabled={busy} testid="cal-event-x" />
        </div>

        {notice && <div style={{ background: '#78350f', color: '#fde68a', padding: '8px 10px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{notice}</div>}
        {error && <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '8px 10px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{error}</div>}
        {isEdit && f.repeat !== 'none' && (
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>
            🔁 Repeating event — changes here apply to the whole series{detail.overrides ? '; single occurrences changed in another app keep their changes' : ''}.
          </div>
        )}
        {isEdit && detail.attendees > 0 && (
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>👥 {detail.attendees} invitee(s) — kept as they are (invites are managed in the app that sent them).</div>
        )}
        {isEdit && !detail.has_master && (
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>This is a single changed occurrence of someone else's series — view only here.</div>
        )}

        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={label} htmlFor="cal-title">Title</label>
            <input id="cal-title" autoFocus={!readOnly} disabled={readOnly} value={f.title} onChange={set('title')} style={input} maxLength={500}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" disabled={readOnly} checked={f.allDay} onChange={set('allDay')} /> All day
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: f.allDay ? '1fr 1fr' : '1fr 110px', gap: 8 }}>
            <div><label style={label}>Start</label><input type="date" disabled={readOnly} value={f.startDate} onChange={set('startDate')} style={input} /></div>
            {!f.allDay && <div><label style={label}>&nbsp;</label><input type="time" disabled={readOnly} value={f.startTime} onChange={set('startTime')} style={input} /></div>}
            {f.allDay && <div><label style={label}>End (last day)</label><input type="date" disabled={readOnly} value={f.endDate} onChange={set('endDate')} style={input} /></div>}
          </div>
          {!f.allDay && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 8 }}>
              <div><label style={label}>End</label><input type="date" disabled={readOnly} value={f.endDate} onChange={set('endDate')} style={input} /></div>
              <div><label style={label}>&nbsp;</label><input type="time" disabled={readOnly} value={f.endTime} onChange={set('endTime')} style={input} /></div>
            </div>
          )}
          {badRange && <div style={{ color: '#f87171', fontSize: 12 }}>The end is before the start.</div>}
          <div style={{ fontSize: 12, color: C.dim }}>{formatRange(times.start, times.end, times.all_day)} · Central time</div>
          <div>
            <label style={label} htmlFor="cal-loc">Location</label>
            <input id="cal-loc" disabled={readOnly} value={f.location} onChange={set('location')} style={input} maxLength={500} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={label} htmlFor="cal-notes">Notes</label>
              {f.notes.trim() !== '' && <ExpandButton onClick={() => setReader(true)} testid="cal-notes-expand" />}
            </div>
            <textarea id="cal-notes" disabled={readOnly} value={f.notes} onChange={set('notes')} rows={4} style={{ ...input, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: mode === 'create' ? '1fr 1fr 1fr' : '1fr 1fr', gap: 8 }}>
            {mode === 'create' && (
              <div>
                <label style={label}>Calendar</label>
                <select value={f.calendarId} onChange={set('calendarId')} style={input} data-testid="cal-choose-calendar">
                  {calendarChoices.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label style={label}>Reminder</label>
              <select disabled={readOnly} value={f.reminder} onChange={set('reminder')} style={input}>
                {f.reminderCustom && <option value="custom">Keep as set in another app</option>}
                {REMINDER_OPTIONS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                {!REMINDER_OPTIONS.some(([v]) => v === f.reminder) && f.reminder !== 'custom' && <option value={f.reminder}>{f.reminder} minutes before</option>}
              </select>
            </div>
            <div>
              <label style={label}>Repeat</label>
              <select disabled={readOnly} value={f.repeat} onChange={set('repeat')} style={input}>
                {initial.repeat === 'custom' && <option value="custom">Custom (kept as is)</option>}
                {REPEAT_OPTIONS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </div>
          </div>
          {isEdit && initial.repeat !== 'none' && f.repeat === 'none' && (
            <div style={{ color: '#fbbf24', fontSize: 12 }}>Turning off repeat keeps only the first occurrence.</div>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 18, alignItems: 'center' }}>
          {!readOnly && <button type="button" style={btn(C.violet, !canSave)} disabled={!canSave} onClick={save} data-testid="cal-save">{busy ? 'Saving…' : 'Save'}</button>}
          {isEdit && !readOnly && (
            <button type="button" style={btn('#b91c1c', busy)} disabled={busy}
              onClick={() => { if (window.confirm(f.repeat !== 'none' ? 'Delete this event and ALL its repeats?' : 'Delete this event?')) onDelete(); }}>Delete</button>
          )}
          {isEdit && cal && cal.kind !== 'office' && (
            <button type="button" style={btn('#15803d', busy)} disabled={busy} onClick={() => onCopy('office')} title="An independent copy — editing one never changes the other">Copy to Office</button>
          )}
          {isEdit && canCopyToMine && (
            <button type="button" style={btn('#1d4ed8', busy)} disabled={busy} onClick={() => onCopy('mine')} title="An independent copy — editing one never changes the other">Copy to my calendar</button>
          )}
          {isEdit && formHref(detail.form_type, detail.form_id) && (    /* 5.42.0 a follow-up from an ACORD form */
            <button type="button" style={btn('#0f766e', false)} onClick={() => openFormInAcord(detail.form_type, detail.form_id)} data-testid="cal-open-form"
              title="Open this form in ACORD">📝 Open form</button>
          )}
          {isEdit && detail.client_id && (    /* 5.38.0 a client follow-up: open that client's folder in InsureDocs */
            <button type="button" style={btn('#7c3aed', false)} onClick={() => openClientInMain(detail.client_id)} data-testid="cal-open-client"
              title="Open this client's folder in InsureDocs">📂 Open client</button>
          )}
          <div style={{ flex: 1 }} />
          <button type="button" style={{ ...btn('transparent', busy), border: `1px solid ${C.border}`, color: C.dim }} disabled={busy} onClick={requestClose}
            data-testid="cal-event-cancel">{readOnly ? 'Close' : 'Cancel'}</button>
        </div>
      </div>
      {reader && (
        <NotesReader title={f.title} when={`${formatRange(times.start, times.end, times.all_day)} · Central time`} notes={f.notes}
          canEdit={!readOnly} saveLabel={mode === 'create' ? 'Done' : 'Save'} onSave={readerSave} onClose={() => setReader(false)} />
      )}
      {prompt}
    </div>
  );
}
